import React, { useState } from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import DialogActions from "@material-ui/core/DialogActions";
import MuiDialogContent from "@material-ui/core/DialogContent";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormControl from "@material-ui/core/FormControl";
import FormLabel from "@material-ui/core/FormLabel";
import Button from "@material-ui/core/Button";

import { StatefulLoadableButton } from "src/components/LoadableButton";
import { featureFlagSelector } from "src/store/system/selector";
import {
  MissingExtensionError,
  OutdatedExtensionError,
} from "src/utils/extension";
import { getWhatnotUser } from "src/utils/extension/whatnot";

import DialogTitle from "../DialogTitle";
import type Step from "../Step";

interface WhatnotDoYouSellOnWhatnotProps {
  onClose: () => void;
  onStepChange: (step: Step) => void;
}

type Options = "yes" | "no";

const useStyles = makeStyles((theme) => ({
  content: {
    margin: "auto",
  },
  radioGroup: {
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(4),
  },
}));

function WhatnotDoYouSellOnWhatnot({
  onClose,
  onStepChange,
}: WhatnotDoYouSellOnWhatnotProps) {
  const classes = useStyles();
  const [value, setValue] = useState<Options>();

  const canAmazonIntegration =
    useSelector((state) => featureFlagSelector(state, "amazon")) &&
    process.env.REACT_APP_MODE === "development";

  return (
    <>
      <DialogTitle onClose={onClose}>Set Up Questions - 5/9</DialogTitle>
      <MuiDialogContent className={classes.content}>
        <FormControl component="fieldset">
          <FormLabel component="legend">Do you sell on Whatnot?</FormLabel>
          <RadioGroup
            value={value || null}
            onChange={(event) => {
              setValue((event.target as HTMLInputElement).value as Options);
            }}
            className={classes.radioGroup}
          >
            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="no" control={<Radio />} label="No" />
          </RadioGroup>
        </FormControl>
      </MuiDialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            onStepChange("mercari:do-you-sell-on-mercari");
          }}
          color="primary"
        >
          Go Back
        </Button>
        <StatefulLoadableButton
          variant="contained"
          disabled={!value}
          onClick={async () => {
            const nextStep = [
              canAmazonIntegration && "amazon:do-you-sell-on-amazon",
              "other:do-you-sell",
            ].filter(Boolean)[0];

            if (value !== "yes") {
              onStepChange(nextStep as Step);
              return;
            }

            try {
              await getWhatnotUser();
              onStepChange("whatnot:confirm");
            } catch (e) {
              if (e instanceof MissingExtensionError) {
                onStepChange("whatnot:add-extension");
                return;
              }

              if (e instanceof OutdatedExtensionError) {
                onStepChange("whatnot:update-extension");
                return;
              }

              toast.error((e as Error).message);
            }
          }}
          color="primary"
          autoFocus
        >
          Next
        </StatefulLoadableButton>
      </DialogActions>
    </>
  );
}

export default WhatnotDoYouSellOnWhatnot;
